import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  Optional,
  Output,
  signal
} from '@angular/core';
import { cloneDeep, isEqual, pullAt } from 'lodash';
import { MapModeStateService } from '../map-mode-selection/map-mode-state.service';
import { QPointMapMode, QPointMapModeScope } from '../map-mode-selection/mode';
import { injectDestroyRef } from '@qpoint/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import PolylineOptions = google.maps.PolylineOptions;

export type QPointMapPolylineType = 'LineString';

export interface QPointMapPolyline {
  type: QPointMapPolylineType;
  /**
   * WGS 84, Array of [Longitude, Latitude] tuples
   */
  coordinates: [number, number][];
  scope?: QPointMapModeScope;
}

export type QPointMapPolylineOptions = Partial<PolylineOptions> & {
  minimumVerticesNumber?: number;
};

export const QPOINT_POLYLINE_DEFAULT_OPTIONS = new InjectionToken<QPointMapPolylineOptions>('Polyline Options');

@Component({
  selector: 'qpoint-map-polyline',
  template: '',
  standalone: true
})
export class MapPolylineComponent implements OnDestroy {
  private destroyRef = injectDestroyRef();
  private readonly _defaultOptions: QPointMapPolylineOptions = this.defaultOptions || {
    strokeOpacity: 1,
    zIndex: 10,
    minimumVerticesNumber: 2,
    strokeColor: '#0B91B1',
    strokeWeight: 5,
    editable: false,
  };
  private editMode: boolean | undefined;
  private subscription: Subscription | null = null;
  private _isActive = signal(false);
  isActive = this._isActive.asReadonly();
  private _scope = signal<QPointMapModeScope>('all');
  scope = this._scope.asReadonly();

  polyline: QPointMapPolyline | null = null;

  @Input('polyline') set polylineSetter(polyline: QPointMapPolyline) {
    this.polyline = cloneDeep(polyline);
    this._scope.set(polyline?.scope ?? 'all');
    this.setupSubscription();
  }

  @Input() options: QPointMapPolylineOptions;
  @Input() manualMapModeActive = false;

  @Output() polylineChanged = new EventEmitter<QPointMapPolyline>();
  @Output() polylineMousemove = new EventEmitter<google.maps.PolyMouseEvent>();
  @Output() polylineMouseout = new EventEmitter<google.maps.PolyMouseEvent>();
  @Output() resetPolylineClicked = new EventEmitter<void>();

  private currentPolylineOptions: QPointMapPolylineOptions | null;

  constructor(@Inject(QPOINT_POLYLINE_DEFAULT_OPTIONS) @Optional() private defaultOptions: QPointMapPolylineOptions,
    private cdr: ChangeDetectorRef, private mapModeState: MapModeStateService) {
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }

  get editable(): boolean {
    return this.manualMapModeActive ? false : this.editMode ?? this.options?.editable ?? this._defaultOptions?.editable ?? false;
  }

  get polylineOptions(): QPointMapPolylineOptions {
    const newOptions = this.createPolylineOptions();

    if (!this.currentPolylineOptions || !isEqual(this.currentPolylineOptions, newOptions)) {
      this.currentPolylineOptions = newOptions;
    }
    return this.currentPolylineOptions;
  }

  get canDeleteVertex(): boolean {
    return this.polylineOptions.editable && this.polyline.coordinates?.length > this.polylineOptions.minimumVerticesNumber;
  }

  private createPolylineOptions(): QPointMapPolylineOptions {
    return {
      ...this._defaultOptions,
      ...this.options,
      editable: this.editable,
      zIndex: this.isActive() ? 100 : this.options?.zIndex ?? this._defaultOptions.zIndex,
      strokeOpacity: this.mapModeState.anyModeActive() && !this.isActive() ? 0.3 : {
        ...this._defaultOptions,
        ...this.options
      }.strokeOpacity,
    };
  }

  deletePolylineVertex(event: google.maps.PolyMouseEvent) {
    if (!this.canDeleteVertex || event.vertex == null) {
      return;
    }

    pullAt(this.polyline.coordinates, event.vertex);
    this.emitAndUpdatePolyline(this.polyline);
  }

  updatePolyline(polyline: QPointMapPolyline) {
    this.emitAndUpdatePolyline(polyline);
  }

  resetPolyline() {
    this.resetPolylineClicked.emit();
  }

  private emitAndUpdatePolyline(polyline: QPointMapPolyline) {
    this.polyline = cloneDeep(polyline);
    this.polylineChanged.emit(cloneDeep(polyline));
    this.cdr.markForCheck();
  }

  private setupSubscription() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscription = this.mapModeState.isModeActive$(QPointMapMode.Polyline, this.scope()).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: isActive => {
        this._isActive.set(isActive);
        this.editMode = !this.manualMapModeActive ? isActive : false;
      }
    });
  }
}
